import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import {
  districtsData,
  districtsDataError,
  streetsNumbersData,
  streetsNumbersDataError,
} from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  useAddResidenceMutation,
  useDeleteResidenceMutation,
  useGetResidenceQuery,
  useUpdateResidenceMutation,
} from '../../../../../redux/services/data/residencesApi';

import { FormTemplate } from '../../FormTemplate';
import { AccordionInputs } from '../../../AccordionInputs';
import { closeDialog, showError } from '../../../../../utils/ui';
import { Loader } from '../../../../ui/Loader';

import {
  booleanValues,
  initialValues,
  mock,
  mockAccordionTitlesResidence,
  validationSchemaResidence,
} from '../../../../../lib/mocks/add-residence-mock';
import {
  getDefaultValues,
  handleResponseError,
} from '../../../../../utils/data';
import {
  errorMessages,
  selectsLocationNames,
} from '../../../../../data/constants';
import { DeleteDialogButton } from '../../../DeleteDialogButton';
import { equipmentInitialValues } from '../../../../../lib/mocks/add-realty-mock/equipment-mock';
import { createFormData } from '../../../../../utils/utils';

export const AddResidenceForm = ({
  isEdit = false,
  formProps: formPropsRealty = {},
  handleClose,
  handleResetInput,
}) => {
  const [error, setError] = useState('');

  const userToken = useSelector(selectUserToken);
  const districts = useSelector(districtsData);
  const districtsError = useSelector(districtsDataError);
  const streetsNumbers = useSelector(streetsNumbersData);
  const streetsNumbersError = useSelector(streetsNumbersDataError);

  const selectValues = useMemo(
    () => ({
      country_id: formPropsRealty?.values[selectsLocationNames.country],
      city_id: formPropsRealty?.values[selectsLocationNames.city],
      street_id: formPropsRealty?.values[selectsLocationNames.street],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formPropsRealty?.values]
  );

  const residenceId = useMemo(
    () => formPropsRealty?.values[selectsLocationNames.residence_id],
    [formPropsRealty?.values]
  );

  const {
    data: residence,
    isFetching: isResidenceFetching,
    error: residenceError,
  } = useGetResidenceQuery(
    { token: userToken, id: residenceId },
    { skip: !isEdit || !residenceId || !userToken }
  );


  const [addNewResidence, { isLoading: isAddingLoading, error: addingError }] =
    useAddResidenceMutation();
  const [editResidence, { isLoading: isEditingLoading, error: editingError }] =
    useUpdateResidenceMutation();
  const [
    deleteResidence,
    { isLoading: isDeletingLoading, error: deletingError },
  ] = useDeleteResidenceMutation();

  const isLoading =
    (isEdit && isResidenceFetching) ||
    isAddingLoading ||
    isEditingLoading ||
    isDeletingLoading;

  // Resolve selected district/street_number objects for defaultValue injection
  const selectedDistrict = useMemo(() => {
    if (!residence?.district_id || !districts?.length) return null;
    const id = residence.district_id?.id ?? residence.district_id?.value ?? residence.district_id;
    return districts.find(
      d => d.id?.toString() === id?.toString() || d.value?.toString() === id?.toString()
    ) ?? null;
  }, [residence?.district_id, districts]);

  const selectedStreetNumber = useMemo(() => {
    if (!residence?.street_number_id || !streetsNumbers?.length) return null;
    const id = residence.street_number_id?.id ?? residence.street_number_id?.value ?? residence.street_number_id;
    return streetsNumbers.find(
      s => s.id?.toString() === id?.toString() || s.value?.toString() === id?.toString()
    ) ?? null;
  }, [residence?.street_number_id, streetsNumbers]);

  // Inject dropdown options into the two dependent selects
  const mockWithData = useMemo(() => {
    return mock.map(item => {
      if (item.name === 'district_id' && districts) {
        return { ...item, selectData: districts };
      }
      if (item.name === 'street_number_id' && streetsNumbers) {
        return { ...item, selectData: streetsNumbers };
      }
      return item;
    });
  }, [districts, streetsNumbers]);

  // Set pre-selected values for edit mode
  const mockWithValues = useMemo(() => {
    if (!isEdit || !residence) return mockWithData;

    // Use getDefaultValues for equipment checkboxes and text/textarea fields,
    // then override the two select fields explicitly with resolved objects.
    const base = getDefaultValues(residence, mockWithData);

    return base.map(item => {
      if (item.name === 'district_id' && selectedDistrict) {
        return { ...item, defaultValue: [selectedDistrict] };
      }
      if (item.name === 'street_number_id' && selectedStreetNumber) {
        return { ...item, defaultValue: [selectedStreetNumber] };
      }
      return item;
    });
  }, [isEdit, residence, mockWithData, selectedDistrict, selectedStreetNumber]);

  // Form initial values — build manually to handle both object and scalar IDs from API
  const formInitialValues = useMemo(() => {
    if (isEdit && residence) {
      const districtId = residence.district_id?.id ?? residence.district_id?.value ?? residence.district_id ?? '';
      const streetNumberId = residence.street_number_id?.id ?? residence.street_number_id?.value ?? residence.street_number_id ?? '';

      return {
        ...initialValues,
        ...selectValues,
        name: residence.name ?? '',
        district_id: districtId?.toString() ?? '',
        street_number_id: streetNumberId?.toString() ?? '',
        is_address_checked: residence.is_address_checked ?? '',
        is_vefa: residence.is_vefa ?? '',
        build_date: residence.build_date ?? '',
        comment: residence.comment ?? '',
        description: residence.description ?? '',
        facilities: residence.facilities?.map(f => f?.value ?? f) ?? [],
        buildings: residence.buildings?.map(b => b?.value ?? b) ?? [],
        securities: residence.securities?.map(s => s?.value ?? s) ?? [],
        sports: residence.sports?.map(s => s?.value ?? s) ?? [],
        exterior_spaces: residence.exterior_spaces?.map(e => e?.value ?? e) ?? [],
      };
    }
    return { ...initialValues, ...selectValues };
  }, [isEdit, residence, selectValues]);

  const handleDelete = async setIsDeleteClick => {
    setIsDeleteClick(false);
    setError('');

    const response = await deleteResidence({
      token: userToken,
      id: residenceId,
    });

    const errorStatus = handleResponseError(
      response?.error,
      deletingError,
      () => closeDialog({ handleClose })
    );

    if (!errorStatus) {
      handleResetInput();
    }

    setError(errorStatus);
  };

  const handleSubmit = async values => {
    setError(null);

    const newValues = { ...values, ...selectValues };

    if (newValues.image && !(newValues.image instanceof File)) {
      delete newValues.image;
    }

    const formData = await createFormData({
      values: newValues,
      keyArrayValues: equipmentInitialValues,
      keyBooleanValues: booleanValues,
    });

    let response;
    let errorStatus;

    if (isEdit) {
      response = await editResidence({
        token: userToken,
        id: residenceId,
        data: formData,
      });

      errorStatus = handleResponseError(response?.error, editingError, () =>
        closeDialog({ handleClose })
      );
    } else {
      response = await addNewResidence({
        token: userToken,
        data: formData,
      });

      errorStatus = handleResponseError(response?.error, addingError, () =>
        closeDialog({ handleClose })
      );
    }

    setError(errorStatus);
  };

  useEffect(() => {
    if (districtsError || streetsNumbersError || residenceError) {
      setError(errorMessages.wentWrong);
    }
  }, [districtsError, streetsNumbersError, residenceError]);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <div className="relative flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      {!isLoading && (
        <FormTemplate
          initialValues={formInitialValues}
          validationSchema={Yup.object(validationSchemaResidence)}
          handleSubmitForm={handleSubmit}
          buttonText={'Save'}
          bgColor={'bg-purpleColor border-purpleColor'}
        >
          {formProps => (
            <>
              <div>
                {Object.entries(mockAccordionTitlesResidence).map(
                  ([key, title]) => (
                    <AccordionInputs
                      title={title}
                      mock={mockWithValues}
                      formProps={formProps}
                      key={key}
                    />
                  )
                )}
              </div>

              {isEdit && (
                <DeleteDialogButton
                  isDeletingLoading={isDeletingLoading}
                  handleDelete={handleDelete}
                  error={error}
                />
              )}
            </>
          )}
        </FormTemplate>
      )}

      {isLoading && (
        <div className="flex items-center justify-center w-full h-[218px]">
          <Loader />
        </div>
      )}
    </div>
  );
};
